import { useEffect, useState, type FormEvent } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/Spinner';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { getErrorMessage } from '@/lib/api-error';
import { EMPTY_FORM_ERRORS, fieldError, formErrorsFromApiError } from '@/lib/form-errors';
import type { EventoPlantilla } from '@/types/domain';
import type { EventoTemplateDef, TemplateCatalog, WhatsappTemplate } from '@/types/templates';
import {
  CATEGORIA_LABELS,
  CATEGORIA_META_LABELS,
  CATEGORIA_META_OPTIONS,
  CATEGORIA_OPTIONS,
  ENCABEZADO_LABELS,
  ENCABEZADO_OPTIONS,
  campoDePosicion,
  campoEtiqueta,
  describeIdentidad,
  eventoLabel,
  templateStatusMeta,
  variablesDelCuerpo,
} from './templates-format';
import {
  TEMPLATE_FORM_FIELDS,
  buildParametros,
  createTemplatePayload,
  emptyTemplateForm,
  templateFormStateFrom,
  updateParametrosPayload,
  updateTemplatePayload,
  type TemplateFormState,
} from './templates-payload';
import { TEMPLATE_LIMITS, validateTemplateForm } from './templates-validation';
import { useCreateTemplate, useUpdateTemplate } from './use-templates';

/**
 * Alta y edición de una plantilla de WhatsApp (módulo 8).
 *
 * El formulario tiene la forma del contrato:
 *  - primero el EVENTO, porque es lo que decide los campos disponibles y los textos sugeridos
 *    (`nombreSugerido`, `cuerpoSugerido`, `parametrosSugeridos`): al crearla, cambiar el evento rellena
 *    el formulario con lo que recomienda el sistema para ese evento (lo mismo que siembra
 *    `POST .../recomendadas`);
 *  - después el nombre (formato de Meta), el idioma, la categoría y el encabezado;
 *  - y al final el cuerpo con su mapeo `{{n}}` → campo, que se recalcula solo cada vez que el cuerpo
 *    cambia (`buildParametros`): el backend rechaza cualquier desajuste entre el mapeo y las variables.
 *
 * LÍMITE DEL CONTRATO: una plantilla que ya salió de `BORRADOR` existe en el WABA y los campos que viven
 * en Meta (nombre, idioma, categoría, encabezado, cuerpo y pie) ya NO se editan desde acá —el backend
 * responde 400— porque este módulo no propaga ediciones a Meta. En ese caso el diálogo deja solo el
 * mapeo (`PATCH` con `parametros`) y avisa dónde se cambia el resto (WhatsApp Manager).
 */

export interface TemplateDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string | null;
  /** Catálogo de eventos (`GET .../eventos`): de ahí salen los campos y los textos sugeridos. */
  catalogo: TemplateCatalog | null;
  /** Plantilla que se edita. `null` = alta. */
  template: WhatsappTemplate | null;
  /** Evento con el que se abre el alta (el botón «Crear» de la tarjeta del catálogo). */
  eventoInicial: EventoPlantilla | null;
}

/**
 * Estado inicial del formulario: la plantilla que se edita o los textos del catálogo para el evento.
 *
 * Vive fuera del componente para poder usarlo como valor inicial perezoso del `useState` (así el
 * formulario ya está armado en el primer render, sin un parpadeo con el formulario vacío) y otra vez en
 * el `useEffect` que lo reinicia cada vez que el diálogo se abre con otra fila.
 */
function formInicial(
  catalogo: TemplateCatalog | null,
  template: WhatsappTemplate | null,
  eventoInicial: EventoPlantilla | null,
): TemplateFormState | null {
  if (!catalogo) return null;

  const evento = template?.evento ?? eventoInicial ?? catalogo.eventos[0]?.evento;
  const def = catalogo.eventos.find((candidato) => candidato.evento === evento);
  if (!def) return null;

  return template
    ? templateFormStateFrom(template, def)
    : emptyTemplateForm(def, catalogo.idiomaPorDefecto);
}

export function TemplateDialog({
  open,
  onClose,
  tenantId,
  catalogo,
  template,
  eventoInicial,
}: TemplateDialogProps) {
  const createTemplate = useCreateTemplate(tenantId);
  const updateTemplate = useUpdateTemplate(tenantId);

  const [form, setForm] = useState<TemplateFormState | null>(() =>
    formInicial(catalogo, template, eventoInicial),
  );
  const [errors, setErrors] = useState(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /** Cada apertura arranca limpia: con los textos del catálogo o con los de la plantilla que se edita. */
  useEffect(() => {
    if (!open || !catalogo) return;

    setForm(formInicial(catalogo, template, eventoInicial));
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
  }, [open, template, eventoInicial, catalogo]);

  const def: EventoTemplateDef | null =
    form && catalogo
      ? (catalogo.eventos.find((evento) => evento.evento === form.evento) ?? null)
      : null;

  const saving = createTemplate.isPending || updateTemplate.isPending;
  const isEditing = template !== null;
  /** La plantilla ya vive en Meta: solo el mapeo (y `activo`, desde la tabla) se puede cambiar. */
  const soloMapeo = isEditing && template.estado !== 'BORRADOR';
  const variables = form ? variablesDelCuerpo(form.cuerpo) : [];

  /** Cambia un campo sin tocar los demás. */
  function patch(changes: Partial<TemplateFormState>) {
    setForm((current) => (current ? { ...current, ...changes } : current));
  }

  /**
   * Cambiar el evento (solo en el alta) rellena el formulario con lo que el sistema recomienda para ese
   * evento: es el mismo texto que siembra `POST .../recomendadas`.
   */
  function cambiarEvento(evento: EventoPlantilla) {
    if (!catalogo || !form || isEditing) return;

    const nuevoDef = catalogo.eventos.find((candidato) => candidato.evento === evento);
    if (!nuevoDef) return;

    setForm(emptyTemplateForm(nuevoDef, form.idioma || catalogo.idiomaPorDefecto));
  }

  /**
   * El cuerpo manda: al editarlo se realinea el mapeo con las variables que quedaron (conservando el
   * campo elegido en las posiciones que siguen existiendo).
   */
  function cambiarCuerpo(cuerpo: string) {
    if (!form || !def) return;
    patch({ cuerpo, parametros: buildParametros(cuerpo, form.parametros, def) });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form || !def) return;

    // El alta valida el formulario completo; la edición, o todo (BORRADOR) o solo el mapeo.
    const nextErrors = validateTemplateForm(form, def);
    const bloqueantes =
      isEditing && soloMapeo
        ? {
            formMessages: nextErrors.formMessages,
            fieldErrors: nextErrors.fieldErrors.parametros
              ? { parametros: nextErrors.fieldErrors.parametros }
              : {},
          }
        : nextErrors;

    if (Object.keys(bloqueantes.fieldErrors).length > 0) {
      setErrors(bloqueantes);
      setSubmitError(null);
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      if (template) {
        await updateTemplate.mutateAsync({
          templateId: template.id,
          payload: soloMapeo ? updateParametrosPayload(form) : updateTemplatePayload(form),
        });
      } else {
        await createTemplate.mutateAsync(createTemplatePayload(form));
      }

      onClose();
    } catch (error) {
      // El backend valida lo mismo (y más): sus mensajes se reparten entre el campo que los produjo y el
      // aviso general del formulario (el `409` del evento + idioma ya registrados, el `400` del mapeo).
      setErrors(formErrorsFromApiError(error, TEMPLATE_FORM_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isEditing ? 'Editar la plantilla' : 'Nueva plantilla de WhatsApp'}
      description={
        isEditing && template
          ? `${describeIdentidad(template)} · ${eventoLabel(template.evento)}`
          : 'Una plantilla por evento e idioma: es la que el bot usa cuando el cliente no escribió en 24 h.'
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          {/* `form` enlaza este botón del pie con el formulario del cuerpo del modal. */}
          <Button type="submit" form="template-form" loading={saving}>
            {isEditing ? 'Guardar cambios' : 'Crear el borrador'}
          </Button>
        </>
      }
    >
      {!catalogo || !form || !def ? (
        <PageLoader label="Cargando el catálogo de eventos…" />
      ) : (
        <form id="template-form" onSubmit={handleSubmit} noValidate className="space-y-5">
          <FormErrorAlert message={submitError} errors={errors} />

          {soloMapeo && template ? (
            <Alert tone="warning">
              Esta plantilla ya está en Meta con estado{' '}
              <strong>{templateStatusMeta(template.estado).label}</strong>: desde acá solo se puede
              guardar el <strong>mapeo de las variables</strong>. El nombre, el idioma, la categoría, el
              encabezado y el cuerpo se editan en WhatsApp Manager; después se sincroniza el estado.
            </Alert>
          ) : null}

          <Field
            label="Evento del sistema"
            htmlFor="templateEvento"
            required
            hint={
              isEditing
                ? 'El evento no se cambia: identifica la plantilla (una por evento e idioma).'
                : 'Al cambiarlo se rellenan el nombre, el cuerpo y el mapeo recomendados para ese evento.'
            }
            error={fieldError(errors, 'evento')}
          >
            <Select
              id="templateEvento"
              name="evento"
              value={form.evento}
              disabled={saving || isEditing || soloMapeo}
              onChange={(event) => cambiarEvento(event.target.value as EventoPlantilla)}
            >
              {catalogo.eventos.map((evento) => (
                <option key={evento.evento} value={evento.evento}>
                  {eventoLabel(evento.evento)} · {CATEGORIA_LABELS[evento.categoria]}
                </option>
              ))}
            </Select>
          </Field>

          <p className="text-caption text-ink-muted">{def.descripcion}</p>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Nombre en Meta"
              htmlFor="templateNombreMeta"
              required
              hint="Minúsculas, números y guiones bajos (así lo exige Meta)."
              error={fieldError(errors, 'nombreMeta')}
            >
              <Input
                id="templateNombreMeta"
                name="nombreMeta"
                placeholder="Ej. pedido_confirmado"
                autoFocus
                maxLength={TEMPLATE_LIMITS.nombreMeta}
                disabled={saving || soloMapeo}
                value={form.nombreMeta}
                invalid={Boolean(fieldError(errors, 'nombreMeta'))}
                onChange={(event) => patch({ nombreMeta: event.target.value })}
              />
            </Field>

            <Field
              label="Idioma"
              htmlFor="templateIdioma"
              required
              hint="`es` o `es_CO` / `en_US`: Meta la busca por nombre + idioma."
              error={fieldError(errors, 'idioma')}
            >
              <Input
                id="templateIdioma"
                name="idioma"
                placeholder="es"
                maxLength={TEMPLATE_LIMITS.idioma}
                disabled={saving || soloMapeo}
                value={form.idioma}
                invalid={Boolean(fieldError(errors, 'idioma'))}
                onChange={(event) => patch({ idioma: event.target.value })}
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Categoría (a quién va)"
              htmlFor="templateCategoria"
              hint="Local: decide en qué casos el sistema usa esta plantilla."
              error={fieldError(errors, 'categoria')}
            >
              <Select
                id="templateCategoria"
                name="categoria"
                value={form.categoria}
                disabled={saving || soloMapeo}
                onChange={(event) =>
                  patch({ categoria: event.target.value as TemplateFormState['categoria'] })
                }
              >
                {CATEGORIA_OPTIONS.map((categoria) => (
                  <option key={categoria} value={categoria}>
                    {CATEGORIA_LABELS[categoria]}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label="Categoría de Meta"
              htmlFor="templateCategoriaMeta"
              hint="Meta puede recategorizarla sola después de revisarla."
              error={fieldError(errors, 'categoriaMeta')}
            >
              <Select
                id="templateCategoriaMeta"
                name="categoriaMeta"
                value={form.categoriaMeta}
                disabled={saving || soloMapeo}
                onChange={(event) =>
                  patch({ categoriaMeta: event.target.value as TemplateFormState['categoriaMeta'] })
                }
              >
                {CATEGORIA_META_OPTIONS.map((categoria) => (
                  <option key={categoria} value={categoria}>
                    {CATEGORIA_META_LABELS[categoria]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Encabezado"
              htmlFor="templateEncabezadoTipo"
              hint={
                def.encabezadoTipo === 'IMAGEN'
                  ? 'Las plantillas de IMAGEN se crean en WhatsApp Manager: Meta exige subir el archivo.'
                  : 'El encabezado de texto es obligatorio si eliges TEXTO.'
              }
              error={fieldError(errors, 'encabezadoTipo')}
            >
              <Select
                id="templateEncabezadoTipo"
                name="encabezadoTipo"
                value={form.encabezadoTipo}
                disabled={saving || soloMapeo}
                onChange={(event) =>
                  patch({ encabezadoTipo: event.target.value as TemplateFormState['encabezadoTipo'] })
                }
              >
                {ENCABEZADO_OPTIONS.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {ENCABEZADO_LABELS[tipo]}
                  </option>
                ))}
              </Select>
            </Field>

            {form.encabezadoTipo === 'TEXTO' ? (
              <Field
                label="Texto del encabezado"
                htmlFor="templateEncabezadoTexto"
                required
                hint={`Hasta ${TEMPLATE_LIMITS.encabezadoTexto} caracteres.`}
                error={fieldError(errors, 'encabezadoTexto')}
              >
                <Input
                  id="templateEncabezadoTexto"
                  name="encabezadoTexto"
                  placeholder="Ej. Tu pedido va en camino"
                  maxLength={TEMPLATE_LIMITS.encabezadoTexto}
                  disabled={saving || soloMapeo}
                  value={form.encabezadoTexto}
                  invalid={Boolean(fieldError(errors, 'encabezadoTexto'))}
                  onChange={(event) => patch({ encabezadoTexto: event.target.value })}
                />
              </Field>
            ) : null}
          </div>

          <Field
            label="Cuerpo"
            htmlFor="templateCuerpo"
            required
            hint="Con las variables posicionales de Meta: {{1}}, {{2}}… Al guardar, el mapeo se ajusta solo a las que queden."
            error={fieldError(errors, 'cuerpo')}
          >
            <Textarea
              id="templateCuerpo"
              name="cuerpo"
              rows={5}
              maxLength={TEMPLATE_LIMITS.cuerpo}
              disabled={saving || soloMapeo}
              value={form.cuerpo}
              invalid={Boolean(fieldError(errors, 'cuerpo'))}
              onChange={(event) => cambiarCuerpo(event.target.value)}
            />
          </Field>

          <div className="space-y-2 rounded-panel border border-line px-3.5 py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-caption font-medium text-ink-soft">Mapeo de las variables</p>
              <p className="text-micro text-ink-faint">
                {variables.length === 0
                  ? 'El cuerpo no tiene variables: no hay nada que mapear.'
                  : 'Cada variable toma su valor de un campo del evento.'}
              </p>
            </div>

            {variables.length > 0 ? (
              <ul className="space-y-2">
                {variables.map((posicion) => {
                  const parametro = campoDePosicion(form.parametros, posicion);

                  return (
                    <li key={posicion} className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <span className="tabular w-14 shrink-0 rounded-control bg-surface-muted px-2 py-1.5 text-center text-caption font-semibold text-ink-soft">
                        {`{{${posicion}}}`}
                      </span>

                      <Select
                        aria-label={`Campo de la variable {{${posicion}}}`}
                        value={parametro?.campo ?? ''}
                        disabled={saving}
                        onChange={(event) =>
                          patch({
                            parametros: form.parametros.map((actual) =>
                              actual.posicion === posicion
                                ? { ...actual, campo: event.target.value }
                                : actual,
                            ),
                          })
                        }
                      >
                        {def.campos.map((campo) => (
                          <option key={campo.clave} value={campo.clave}>
                            {campoEtiqueta(def.campos, campo.clave)} — ej. {campo.ejemplo}
                          </option>
                        ))}
                      </Select>
                    </li>
                  );
                })}
              </ul>
            ) : null}

            {fieldError(errors, 'parametros') ? (
              <p className="text-micro text-danger-strong">{fieldError(errors, 'parametros')}</p>
            ) : null}

            <p className="text-micro text-ink-faint">
              Campos de {eventoLabel(form.evento)}:{' '}
              {def.campos
                .map((campo) => `${campo.clave} (${campoEtiqueta(def.campos, campo.clave)})`)
                .join(' · ')}
            </p>
          </div>


          <Field
            label="Pie"
            htmlFor="templatePie"
            hint={`Opcional (hasta ${TEMPLATE_LIMITS.pie} caracteres).`}
            error={fieldError(errors, 'pie')}
          >
            <Input
              id="templatePie"
              name="pie"
              maxLength={TEMPLATE_LIMITS.pie}
              disabled={saving || soloMapeo}
              value={form.pie}
              invalid={Boolean(fieldError(errors, 'pie'))}
              onChange={(event) => patch({ pie: event.target.value })}
            />
          </Field>

          {!isEditing ? (
            <div className="flex items-center justify-between gap-3 rounded-panel border border-line px-3.5 py-3">
              <div className="min-w-0">
                <p className="text-caption font-medium text-ink-soft">Mandar a revisión de Meta</p>
                <p className="text-micro text-ink-faint">
                  Crea la plantilla en el WABA al guardar (queda PENDIENTE y sin activar). Necesita el
                  canal de WhatsApp conectado, y Meta limita a 100 por hora. Las de encabezado de IMAGEN
                  se crean en WhatsApp Manager.
                </p>
              </div>

              <span className="shrink-0">
                <Switch
                  id="templateEnviarAMeta"
                  checked={form.enviarAMeta}
                  label="Mandar la plantilla a revisión de Meta al guardar"
                  disabled={saving || form.encabezadoTipo === 'IMAGEN'}
                  onChange={(enviarAMeta) => patch({ enviarAMeta })}
                />
              </span>
            </div>
          ) : null}


        </form>
      )}
    </Modal>
  );
}


